import { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import TextInput from "../TextInput/TextInput";
import CustomSelect from "../Select/Select";
import Checkbox from "../Checkbox/Checkbox";
import {
  PresetListModel,
  ProcessingPresetForm,
} from "@/database";

interface Props {
  presetList?: PresetListModel[];
  genre?: any;
  item?: any;
  onSubmit?: (payload: any) => void;
  Language_Options?: any;
  businessProductConstants?: any;
  business?: any;
  readOnlyMode?: boolean;
  isPreview?: boolean;
  isEdit?: boolean;
  setIsSocialBtnDisabled?: (isDisabled: boolean) => void;
  bunchProductList?: any;
}

const ProcessingPreset = ({
  presetList,
  item,
  onSubmit,
  Language_Options,
  bunchProductList,
  readOnlyMode = false,
  isPreview = false,
  isEdit = false,
  setIsSocialBtnDisabled,
}: Props) => {
  const audioPresets = presetList?.filter(
    (preset: PresetListModel) => preset.sub_type === "audio"
  );
  const videoPresets = presetList?.filter(
    (preset: PresetListModel) => preset.sub_type === "video"
  );

  const [isTranscriptEnabled, setIsTranscriptEnabled] = useState(false);

  const [enabledFields, setEnabledFields] = useState<Record<string, boolean>>({
    video_flavour: false,
    audio_flavour: false,
    encryption: false,
    generate_video_preview: false,
    generate_ai_thumbnail: false,
    generate_embedded_podcast: false,
    audio_waveform: false,
    generate_transcript: false,
  });

  const handleFieldToggle = (fieldName: string, checked: boolean) => {
    setEnabledFields((prev) => ({
      ...prev,
      [fieldName]: checked,
    }));
  };

  const getEnabledFieldsPayload = (values: any) => {
    const payload: any = {
      presetName: values.presetName, // Always include preset name
      visibility: values.visibility,
    };

    Object.keys(enabledFields).forEach((fieldName) => {
      if (
        enabledFields[fieldName] &&
        values[fieldName] !== undefined &&
        values[fieldName] !== ""
      ) {
        if (Array.isArray(values[fieldName])) {
          if (values[fieldName].length > 0) {
            payload[fieldName] = values[fieldName];
          }
        } else {
          payload[fieldName] = values[fieldName];
        }
      }
    });

    return payload;
  };

  useEffect(() => {
    if (item) {
      const initialEnabledFields: Record<string, boolean> = {
        presetName: !!item.presetName,
        visibility: !!item.visibility,
      };
      setEnabledFields(initialEnabledFields);
    }
  }, [item]);

  const formik = useFormik<ProcessingPresetForm>({
    validateOnChange: true,
    initialValues: {
      presetName: item?.name || "",
      visibility: item?.team || [],
      status: item?.status || "",
      video_flavour: item?.processing?.[0]?.video_flavour || "",
      audio_flavour: item?.processing?.[0]?.audio_flavour || "",
      encryption: item?.processing?.[0]?.encryption || "",
      generate_video_preview:
        item?.processing?.[0]?.generate_video_preview || false,
      generate_ai_thumbnail:
        item?.processing?.[0]?.generate_ai_thumbnail || false,
      generate_embedded_podcast:
        item?.processing?.[0]?.generate_embedded_podcast || false,
      audio_waveform: item?.processing?.[0]?.audio_waveform || false,
      generate_transcript: item?.processing?.[0]?.generate_transcript || false,
      language: item?.processing?.[0]?.language || "",
    },
    validationSchema: Yup.object({
      presetName: Yup.string()
        .required("Preset Name is required")
        .test(
          "unique-preset-name",
          "Same Name is not allowed",
          function (value) {
            if (!value || !presetList || presetList.length === 0) return true;

            const currentVisibility = this.parent.visibility || [];

            const duplicateExists = presetList.some((presetItem: any) => {
              // Check if names match
              if (presetItem.name !== value) return false;

              // Check if type matches
              if (presetItem.type !== "processing") return false;

              // Skip self in edit mode
              if (isEdit && presetItem.id === item?.id) return false;

              // Check visibility/team match - compare arrays properly
              const itemteam = presetItem.team || [];

              // If item has no team, don't match  s
              if (itemteam.length === 0) {
                return false;
              }

              // Check if any visibility value matches any team value
              return currentVisibility.some((vis: string) =>
                itemteam.includes(vis)
              );
            });

            return !duplicateExists;
          }
        ),
      visibility: Yup.array().of(Yup.string()).min(1, "team is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        const error = errors["presetName" as keyof typeof errors];
        formik.setFieldError(
          "presetName",
          Array.isArray(error) ? error.join(", ") : error
        );
        formik.setFieldTouched("presetName", true);

        return;
      }
      const payload = getEnabledFieldsPayload(values);

      if (onSubmit) {
        onSubmit(formik.values);
      }
    },
  });

  useEffect(() => {
    if (
      formik.values.presetName &&
      formik.values.visibility &&
      formik.values.status
    ) {
      setIsSocialBtnDisabled?.(false);
    } else {
      setIsSocialBtnDisabled?.(true);
    }
  }, [
    formik.values.presetName,
    formik.values.visibility,
    formik.values.status,
    setIsSocialBtnDisabled,
  ]);

  const handleCreateMetaPreset = useCallback(() => {
    formik.handleSubmit();
  }, [formik, enabledFields]);

  const getBasePermission = (permission: string): string => {
    return permission;
  };

  function processPermissionForSelect(array: string[]) {
    const newarray = array?.map((str) => {
      return getBasePermission(str);
    });
    return newarray;
  }
  const deepEqualIgnoreExtraKeys = (a: any, b: any): boolean => {
    // If both are arrays → compare item by item
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => deepEqualIgnoreExtraKeys(item, b[index]));
    }

    // If both are objects → compare only keys present in `a`
    if (typeof a === "object" && typeof b === "object" && a && b) {
      return Object.keys(a).every((key) =>
        deepEqualIgnoreExtraKeys(a[key], b[key])
      );
    }

    // Fallback: direct comparison
    return a === b;
  };
  useEffect(() => {
    const initialValues = formik.initialValues;
    const currentValues = formik.values;

    const basicFieldsHaveChanges = !deepEqualIgnoreExtraKeys(
      initialValues,
      currentValues
    );
    const shouldEnableButton = basicFieldsHaveChanges;
    setIsSocialBtnDisabled?.(!shouldEnableButton);
  }, [formik.values, formik.initialValues, setIsSocialBtnDisabled]);

  const handlePermissionChange = (values: string[], key: string) => {
    const newProducers = values?.map((value) => value);

    formik.setFieldValue(key, newProducers);
  };

  useEffect(() => {
    window.addEventListener("createMetaPreset", handleCreateMetaPreset);
    return () =>
      window.removeEventListener("createMetaPreset", handleCreateMetaPreset);
  }, [handleCreateMetaPreset]);

  return (
    <>
      <div
        className="row row-margin gx-2 gy-3"
        style={{ backgroundColor: "#FCFAFA" }}
      >
        {!isPreview && (
          <div className="col-md-4 mt-0">
            <TextInput
              label="Preset Name"
              name="presetName"
              value={formik.values.presetName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              showRequired={true}
              error={formik.errors.presetName as string}
              touched={formik.touched.presetName as boolean}
            />
          </div>
        )}
        {!isPreview && (
          <div className="col-md-4 mt-0">
            <CustomSelect
              value={processPermissionForSelect(formik.values.visibility)}
              handleChange={(newValue: any) => {
                handlePermissionChange(
                  newValue.map((option: any) => option.value),
                  "visibility"
                );
              }}
              label="team"
              multiSelect={true}
              placeholder="Team/Property"
              checkBox={true}
              showRequired={true}
              options={
                bunchProductList
                  ?.filter(
                    (item: any) =>
                      item?.access === "admin" || item?.access === "write"
                  )
                  ?.map((item: any) => ({
                    label: item?.name,
                    value: item?.id,
                  })) || []
              }
              updatedOptions={
                bunchProductList
                  ?.filter(
                    (item: any) =>
                      item?.access === "admin" || item?.access === "write"
                  )
                  ?.map((item: any) => ({
                    label: item?.name,
                    value: item?.id,
                  })) || []
              }
              hideArrow={true}
              error={formik.errors.visibility as string}
              touched={formik.touched.visibility as boolean}
              handleBlur={formik.handleBlur}
            />
          </div>
        )}
        {!isPreview && (
          <div className="col-md-4 mt-0">
            <CustomSelect
              value={formik.values.status}
              handleChange={(newValue: any) => {
                formik.setFieldValue("status", newValue.value);
              }}
              label="Status"
              multiSelect={false}
              placeholder="Select Status"
              showRequired={true}
              options={[
                {
                  label: "Active",
                  value: 1,
                },
                {
                  label: "Inactive",
                  value: 2,
                },
              ]}
              updatedOptions={[
                { label: "Active", value: "1" },
                { label: "Inactive", value: "2" },
              ]}
              hideArrow={true}
              error={formik.errors.status as string}
              touched={formik.touched.status as boolean}
            />
          </div>
        )}
      </div>
      <div
        className=" mb-4 mt-4 position-relative pt-2 "
        style={{ minHeight: "202px" }}
      >
        <div className="row  px-2 py-2">
          <div className="col-md-4 mb-3">
            <CustomSelect
              label="Video Flavour"
              value={formik.values.video_flavour}
              options={
                videoPresets && videoPresets?.length > 0
                  ? videoPresets.map((preset: PresetListModel) => ({
                      label: preset.name || "",
                      value: preset.id || "",
                    }))
                  : [{ label: "1080", value: "1080" }]
              }
              handleChange={(newValue: any) =>
                formik.setFieldValue("video_flavour", newValue.value)
              }
              placeholder={"Select Video Flavour"}
              showRequired={false}
              error={formik.errors.video_flavour}
              touched={formik.touched.video_flavour as boolean}
              handleBlur={formik.handleBlur}
              labelCheckbox={false}
              labelCheckboxChecked={enabledFields.video_flavour}
              onLabelCheckboxChange={(checked: boolean) => {
                handleFieldToggle("video_flavour", checked);
              }}
              disableLabelCheckbox={
                isPreview && !(formik.values.video_flavour.length > 0)
              }
              readOnlyMode={readOnlyMode}
              disabled={isPreview}
            />
          </div>

          <div className="col-md-4 mb-3">
            <CustomSelect
              label="Audio Flavour"
              value={formik.values.audio_flavour}
              options={
                audioPresets && audioPresets?.length > 0
                  ? audioPresets.map((preset: PresetListModel) => ({
                      label: preset.name || "",
                      value: preset.id || "",
                    }))
                  : [{ label: "320 kbps", value: "320" }]
              }
              handleChange={(newValue: any) =>
                formik.setFieldValue("audio_flavour", newValue.value)
              }
              placeholder={"Select Audio Flavour"}
              showRequired={false}
              error={formik.errors.audio_flavour}
              touched={formik.touched.audio_flavour as boolean}
              handleBlur={formik.handleBlur}
              labelCheckbox={false}
              labelCheckboxChecked={enabledFields.audio_flavour}
              onLabelCheckboxChange={(checked: boolean) => {
                handleFieldToggle("audio_flavour", checked);
              }}
              disableLabelCheckbox={
                isPreview && !(formik.values.audio_flavour.length > 0)
              }
              readOnlyMode={readOnlyMode}
              disabled={isPreview}
            />
          </div>

          <div className="col-md-4 mb-3">
            <CustomSelect
              label="Encryption"
              value={formik.values.encryption ? "yes" : "no"}
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
              ]}
              handleChange={(newValue: any) =>
                formik.setFieldValue("encryption", newValue.value === "yes")
              }
              placeholder={"Encryption"}
              showRequired={false}
              error={formik.errors.encryption}
              touched={formik.touched.encryption as boolean}
              handleBlur={formik.handleBlur}
              labelCheckbox={false}
              labelCheckboxChecked={enabledFields.encryption}
              onLabelCheckboxChange={(checked: boolean) => {
                handleFieldToggle("encryption", checked);
              }}
              disableLabelCheckbox={
                isPreview && !(formik.values.encryption.length > 0)
              }
              disabled={isPreview}
              readOnlyMode={readOnlyMode}
            />
          </div>
        </div>

        <div className="row px-2 py-2 ">
          <div className="col-md-4 mb-4">
            <Checkbox
              checked={formik.values.generate_video_preview}
              onChange={(e: any) =>
                formik.setFieldValue("generate_video_preview", e.target.checked)
              }
              label="Generate Video Preview"
              error={formik.errors.generate_video_preview}
              touched={formik.touched.generate_video_preview as boolean}
              disabled={isPreview}
              readOnlyMode={readOnlyMode}

              // required={mediaFeatures['video_preview'].required}
            />
          </div>

          <div className="col-md-4 mb-4">
            <Checkbox
              checked={formik.values.generate_ai_thumbnail}
              onChange={(e: any) =>
                formik.setFieldValue("generate_ai_thumbnail", e.target.checked)
              }
              label="Generate AI Thumbnail"
              error={formik.errors.generate_ai_thumbnail}
              touched={formik.touched.generate_ai_thumbnail}
              disabled={isPreview}
              readOnlyMode={readOnlyMode}
              // required={mediaFeatures['ai_thumbnail'].required}
            />
          </div>

          <div className="col-md-4 mb-4">
            <Checkbox
              checked={formik.values.generate_embedded_podcast}
              onChange={(e: any) =>
                formik.setFieldValue(
                  "generate_embedded_podcast",
                  e.target.checked
                )
              }
              label="Generate Embedded Podcast"
              error={formik.errors.generate_embedded_podcast}
              touched={formik.touched.generate_embedded_podcast}
              disabled={isPreview}
              readOnlyMode={readOnlyMode}
              // required={mediaFeatures['embedded_podcast'].required}
            />
          </div>

          <div className="col-md-4 mb-4">
            <Checkbox
              checked={formik.values.audio_waveform}
              onChange={(e: any) =>
                formik.setFieldValue("audio_waveform", e.target.checked)
              }
              label="Generate Audio Waveform"
              error={formik.errors.audio_waveform}
              touched={formik.touched.audio_waveform}
              disabled={isPreview}
              readOnlyMode={readOnlyMode}
              // required={mediaFeatures['audio_waveform'].required}
            />
          </div>

          <div className="col-md-4 mb-4">
            <Checkbox
              checked={formik.values.generate_transcript}
              onChange={(e: any) => {
                formik.setFieldValue("generate_transcript", e.target.checked);
                setIsTranscriptEnabled(e.target.checked);
              }}
              label="Generate Transcript"
              error={formik.errors.generate_transcript}
              touched={formik.touched.generate_transcript}
              disabled={isPreview}
              readOnlyMode={readOnlyMode}
              // required={mediaFeatures['transcript'].required}
            />
          </div>

          <div className="col-md-4 mb-4" style={{ marginTop: "-12px" }}>
            <CustomSelect
              name="language"
              placeholder="Select Language"
              value={formik.values.language}
              options={Language_Options}
              handleChange={(newValue: any) =>
                formik.setFieldValue("language", newValue.value)
              }
              label=""
              disabled={!formik.values.generate_transcript}
              className={!formik.values.generate_transcript ? "opacity-50" : ""}
              error={formik.errors.language}
              touched={formik.touched.language as boolean}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProcessingPreset;
